// FILE: src/services/studyPlanService.js
//
// Phase D — Study Schedule rule engine + persistence.
//
// Pure `generateStudyPlan({ examId, today, targetDate, readiness, examConfig })`
// emits an ordered list of activities (one per calendar day between today
// and targetDate). The rule engine implements §5 of PLAN-phase-a-design.md:
//
//   • Normal mode (≥ 14 days):
//       Foundation phase (first ~50% if readiness ≤ building_foundation):
//         60% practice / 20% targeted (weak phases) / 10% review / 10% rest
//       Practice phase  (next ~30%):
//         40% practice / 30% targeted / 20% full sim / 10% review
//       Polish phase    (last ~20%):
//         30% full sim / 30% targeted weak / 30% review / 10% rest
//     Plus hard rules:
//       - At least one full simulation by day +14 if days ≥ 30.
//       - Reach examConfig.target_full_sims by the final week.
//
//   • Final Sprint mode (3 ≤ days < 14):
//       2 full simulations + targeted weak + review only.
//       No new content.
//
//   • Recovery mode (< 3 days):
//       Light review + 1 timed practice. No full sims.
//
//   • Weekly cadence (study_cadence per cert):
//       <days_on> study days → <review_days> review → <rest_days> rest.
//       Hard-rule full sims override the cadence cell on their scheduled day.
//
// Frozen-on-save (§5): the engine runs at save time. Editing = manual checkoff
// or explicit Regenerate.
//
// Tone (§6.5): no streak/engagement copy; activities are coaching suggestions,
// never quotas.

import { supabase } from './supabaseClient';
import { readingListFor, readingTaskKey } from './studyPlanReading';
import { logisticsTasksForExam } from './examLogisticsService';

// ── Activity types ──────────────────────────────────────────────────────────

export const ACTIVITY = {
  PRACTICE:  'practice',
  TARGETED:  'targeted',
  FULL:      'full',
  REVIEW:    'review',
  REST:      'rest',
  READING:   'reading',
  LOGISTICS: 'logistics',
  EXAM_DAY:  'exam_day',
};

// ── Public — pure rule engine ───────────────────────────────────────────────

/**
 * @param {object} params
 * @param {string} params.examId
 * @param {Date|string} params.today
 * @param {Date|string} params.targetDate
 * @param {object} [params.readiness]  — computeReadiness() result, optional
 * @param {object} params.examConfig    — getExamConfig(examId)
 * @returns {{ activities: object[], mode: string, daysUntilExam: number, weakPhases: string[] }}
 */
export function generateStudyPlan({ examId, today, targetDate, readiness, examConfig }) {
  const start  = toDate(today);
  const target = toDate(targetDate);
  if (!start || !target) {
    return { activities: [], mode: 'invalid', daysUntilExam: 0, weakPhases: [] };
  }

  const daysUntil = daysBetween(start, target);
  if (daysUntil < 0) {
    return { activities: [], mode: 'past', daysUntilExam: daysUntil, weakPhases: [] };
  }

  const cadence = (examConfig && examConfig.study_cadence) || { days_on: 5, review_days: 1, rest_days: 1 };
  const targetFullSims = (examConfig && examConfig.target_full_sims) || 3;
  // Full-simulation length is the cert's real exam duration — never hardcoded.
  const fullSimMinutes =
    (examConfig && examConfig.fullExam && examConfig.fullExam.durationMinutes) || 240;
  const weakPhases = extractWeakPhases(readiness);
  const status = (readiness && readiness.status) || 'not_started';

  // Mode selection
  let mode;
  if (daysUntil < 3)        mode = 'recovery';
  else if (daysUntil < 14)  mode = 'final_sprint';
  else                       mode = 'normal';

  let activities;
  if (mode === 'recovery') {
    activities = buildRecoveryPlan(examId, start, daysUntil, weakPhases);
  } else if (mode === 'final_sprint') {
    activities = buildFinalSprintPlan(examId, start, daysUntil, weakPhases, fullSimMinutes);
  } else {
    activities = buildNormalPlan(examId, start, daysUntil, status, weakPhases, cadence, targetFullSims, fullSimMinutes);
  }

  // Command Center additions (migration 069):
  //   • Normal mode interleaves front-loaded official READING tasks.
  //   • Date-pinned LOGISTICS tasks are added alongside the day's activity.
  //   • An EXAM_DAY item lands on the target date itself.
  // Final-sprint / recovery stay quiz+review-centric (no new content late).
  if (mode === 'normal') {
    activities = interleaveReading(examId, activities);
  }
  activities = activities.concat(buildLogisticsItems(examId, start, daysUntil, examConfig));
  activities.push(activity({
    examId, date: target, type: ACTIVITY.EXAM_DAY,
    title: 'Exam day',
    description: 'Arrive early (or log in early if remote), ID ready. Trust the preparation.',
  }));

  return { activities, mode, daysUntilExam: daysUntil, weakPhases };
}

// ── Command Center items ────────────────────────────────────────────────────

/**
 * Replace an early subset of PRACTICE study days with the cert's official
 * reading queue: every 2nd practice day until the queue is exhausted.
 * Deterministic (index-based), so regenerating with the same inputs yields
 * the same plan.
 */
function interleaveReading(examId, acts) {
  const queue = [...readingListFor(examId)];
  if (queue.length === 0) return acts;
  // Adaptive density: default cadence is every 2nd practice day, but when
  // the catalog is long relative to the runway (e.g. PMP's 14 items on a
  // 60-day plan), use every practice day so the whole reading list drains
  // by the end of the practice bucket. Targeted/full/review days keep the
  // plan varied either way. Deterministic — density depends only on inputs.
  const practiceDays = acts.filter((a) => a.activity_type === ACTIVITY.PRACTICE).length;
  const everyDay = queue.length > Math.floor(practiceDays / 2);
  let practiceSeen = 0;
  return acts.map((a) => {
    if (queue.length === 0 || a.activity_type !== ACTIVITY.PRACTICE) return a;
    practiceSeen += 1;
    if (everyDay || practiceSeen % 2 === 1) {
      const r = queue.shift();
      return {
        ...a,
        activity_type:     ACTIVITY.READING,
        recommended_count: null,
        target_minutes:    r.minutes || 30,
        description:       `Official reading — ${r.source}.`,
        title:             r.title,
        source_reference:  r.source,
        linked_domain:     r.linkedDomain || null,
        linked_task:       readingTaskKey(r.refId),
      };
    }
    return a;
  });
}

/**
 * Date-pinned logistics items from the LOGISTICS_TASKS catalog. Only tasks
 * with a due offset that lands inside the plan window are scheduled (2 days
 * ahead of the recommended-by date); tasks without a deadline land at the
 * ~20% mark. These are ADDITIVE — a logistics row shares its date with the
 * day's study activity.
 */
function buildLogisticsItems(examId, start, daysUntil, examConfig) {
  const defs = logisticsTasksForExam(examId);
  const items = [];
  for (const t of defs) {
    if (t.when) continue; // conditional ("If remote") — checklist-only, never scheduled
    let dayIdx;
    if (t.dueOffsetDays == null) {
      dayIdx = Math.min(Math.max(1, Math.floor(daysUntil * 0.2)), Math.max(daysUntil - 1, 0));
    } else {
      dayIdx = daysUntil - t.dueOffsetDays - 2;
      if (dayIdx >= daysUntil) dayIdx = daysUntil - 1;
      if (dayIdx < 0) dayIdx = 0; // already inside the window — schedule ASAP
    }
    items.push(activity({
      examId, date: addDays(start, dayIdx), type: ACTIVITY.LOGISTICS,
      minutes: 15,
      title: t.title,
      description: t.sub ? (t.sub(examId) || '') : '',
      linkedTask: t.key,
    }));
  }
  return items;
}

// ── Mode: Recovery (< 3 days) ───────────────────────────────────────────────

function buildRecoveryPlan(examId, start, daysUntil, weakPhases) {
  const acts = [];
  // Day 0: light review.
  acts.push(activity({ examId, date: start, type: ACTIVITY.REVIEW,
    description: 'Light review — skim your weakest phase notes.', minutes: 20 }));
  // Day 1: a single timed practice on the weakest phase.
  if (daysUntil >= 1) {
    const d1 = addDays(start, 1);
    acts.push(activity({
      examId, date: d1, type: ACTIVITY.TARGETED,
      focusPhase: weakPhases[0] || null,
      count: 15, minutes: 20,
      description: weakPhases[0]
        ? `Timed targeted practice — ${weakPhases[0]} (15 questions).`
        : 'Timed targeted practice (15 questions).',
    }));
  }
  // Day 2 (eve of exam): rest.
  if (daysUntil >= 2) {
    acts.push(activity({ examId, date: addDays(start, 2), type: ACTIVITY.REST,
      description: 'Rest the day before — trust the preparation.' }));
  }
  return acts;
}

// ── Mode: Final Sprint (3-13 days) ──────────────────────────────────────────

function buildFinalSprintPlan(examId, start, daysUntil, weakPhases, fullSimMinutes = 240) {
  const acts = [];
  // Two full simulations spaced through the window. Avoid the last day.
  const simSlots = pickSimSlots(daysUntil);
  // Targeted weak-phase sessions on most other days, review on a few, last day rest.
  for (let i = 0; i < daysUntil; i++) {
    const date = addDays(start, i);
    const isLast = i === daysUntil - 1;

    if (isLast) {
      acts.push(activity({ examId, date, type: ACTIVITY.REST,
        description: 'Rest the day before — trust the preparation.' }));
      continue;
    }
    if (simSlots.includes(i)) {
      acts.push(activity({
        examId, date, type: ACTIVITY.FULL,
        minutes: fullSimMinutes,
        description: 'Full-length simulation — pace yourself; aim for steady focus.',
      }));
      continue;
    }
    // Alternate targeted / review across the remaining days.
    if (i % 2 === 0) {
      const phase = weakPhases[(i / 2) % Math.max(1, weakPhases.length)] || null;
      acts.push(activity({
        examId, date, type: ACTIVITY.TARGETED,
        focusPhase: phase,
        count: 15, minutes: 20,
        description: phase
          ? `Targeted practice — ${phase} (15 questions).`
          : 'Targeted practice (15 questions).',
      }));
    } else {
      acts.push(activity({
        examId, date, type: ACTIVITY.REVIEW,
        minutes: 30,
        description: 'Review notes + flagged questions from recent sessions.',
      }));
    }
  }
  return acts;
}

function pickSimSlots(daysUntil) {
  // Two simulations: roughly at the 30% and 70% marks, but not on the last day.
  if (daysUntil < 5) return [Math.floor(daysUntil / 2)];
  const a = Math.max(1, Math.floor(daysUntil * 0.3));
  const b = Math.max(a + 2, Math.floor(daysUntil * 0.7));
  const safe = (i) => Math.min(i, daysUntil - 2);
  return Array.from(new Set([safe(a), safe(b)])).sort((x, y) => x - y);
}

// ── Mode: Normal (≥ 14 days) ────────────────────────────────────────────────

function buildNormalPlan(examId, start, daysUntil, readinessStatus, weakPhases, cadence, targetFullSims, fullSimMinutes = 240) {
  // Bucket boundaries (in days from start).
  const foundationEnabled = readinessStatus === 'not_started' || readinessStatus === 'building_foundation';
  const foundationDays = foundationEnabled ? Math.floor(daysUntil * 0.5) : 0;
  const practiceDays   = Math.floor(daysUntil * 0.3);
  // Polish takes the remaining days.
  const polishDays     = Math.max(daysUntil - foundationDays - practiceDays, 0);

  // Pre-mark hard-rule full simulations.
  const simDays = pickNormalSimDays(daysUntil, foundationDays, practiceDays, polishDays, targetFullSims, cadence);

  const acts = [];
  let weakIdx = 0; // cycles through weakPhases

  for (let i = 0; i < daysUntil; i++) {
    const date = addDays(start, i);
    const cell = cadenceCell(i, cadence);  // 'study' | 'review' | 'rest'

    // Hard-rule sim overrides anything on its slot, except rest? §5 implies
    // sims happen on study days. We place sims on cadence='study' slots.
    if (simDays.includes(i) && cell === 'study') {
      acts.push(activity({
        examId, date, type: ACTIVITY.FULL, minutes: fullSimMinutes,
        description: 'Full-length simulation — pace yourself; aim for steady focus.',
      }));
      continue;
    }

    if (cell === 'review') {
      acts.push(activity({ examId, date, type: ACTIVITY.REVIEW, minutes: 30,
        description: 'Review notes + flagged questions from recent sessions.' }));
      continue;
    }
    if (cell === 'rest') {
      acts.push(activity({ examId, date, type: ACTIVITY.REST,
        description: 'Rest day — let recall consolidate.' }));
      continue;
    }

    // Study-day — pick activity type based on bucket distribution.
    const bucket = bucketAt(i, foundationDays, practiceDays);
    const studyType = pickStudyType(bucket, i);

    if (studyType === ACTIVITY.PRACTICE) {
      acts.push(activity({ examId, date, type: ACTIVITY.PRACTICE,
        count: 20, minutes: 30,
        description: 'Practice session — randomized across phases (20 questions).' }));
    } else if (studyType === ACTIVITY.TARGETED) {
      const phase = weakPhases[weakIdx % Math.max(1, weakPhases.length)] || null;
      weakIdx++;
      acts.push(activity({ examId, date, type: ACTIVITY.TARGETED,
        focusPhase: phase, count: 15, minutes: 20,
        description: phase
          ? `Targeted practice — ${phase} (15 questions).`
          : 'Targeted practice (15 questions).' }));
    } else if (studyType === ACTIVITY.FULL) {
      acts.push(activity({ examId, date, type: ACTIVITY.FULL, minutes: fullSimMinutes,
        description: 'Full-length simulation — pace yourself; aim for steady focus.' }));
    } else {
      acts.push(activity({ examId, date, type: ACTIVITY.REVIEW, minutes: 30,
        description: 'Review notes + flagged questions from recent sessions.' }));
    }
  }
  return acts;
}

/**
 * Pre-schedule the full sims to satisfy the hard rules:
 *   - one full sim by day +14 if daysUntil >= 30
 *   - reach target_full_sims by the start of the final week
 * Sims fall on study-day cadence cells; if a chosen index isn't a study
 * cell, we shift forward to the next study cell.
 */
function pickNormalSimDays(daysUntil, foundationDays, practiceDays, polishDays, targetFullSims, cadence) {
  // Sims must land on 'study' cadence cells — buildNormalPlan only places a sim
  // when its day is a study cell, so a sim scheduled on a review/rest cell would
  // be silently dropped (the old bug). Snap each chosen day BACKWARD to the
  // nearest study cell so the "first sim by day +14" deadline and the rest are
  // honored rather than missed.
  const snap = (day) => studyCellAtOrBefore(Math.min(day, daysUntil - 1), cadence);
  const sims = [];

  const minFirstSim = daysUntil >= 30 ? Math.min(13, daysUntil - 2) : null;
  if (minFirstSim != null) sims.push(snap(minFirstSim));

  // Remaining sims spaced through the practice + polish buckets.
  const remaining = Math.max(0, targetFullSims - sims.length);
  const startBucket = foundationDays;
  const polishStart = foundationDays + practiceDays;
  const lastSimDeadline = Math.max(polishStart + Math.floor(polishDays / 2), startBucket + 2);

  if (remaining > 0 && lastSimDeadline > startBucket) {
    for (let r = 0; r < remaining; r++) {
      const target = Math.min(
        startBucket + Math.floor(((r + 1) * (lastSimDeadline - startBucket)) / (remaining + 1)),
        daysUntil - 2,
      );
      const slot = snap(target);
      if (!sims.includes(slot)) sims.push(slot);
    }
  }
  return sims.filter((i) => i >= 0 && i < daysUntil).sort((a, b) => a - b);
}

function bucketAt(dayIndex, foundationDays, practiceDays) {
  if (dayIndex < foundationDays) return 'foundation';
  if (dayIndex < foundationDays + practiceDays) return 'practice';
  return 'polish';
}

// Per-bucket distribution. Returns one activity type at a time deterministically
// using the day index so the plan is stable on regen for the same inputs.
function pickStudyType(bucket, dayIndex) {
  const order =
    bucket === 'foundation'
      ? [
          ACTIVITY.PRACTICE, ACTIVITY.PRACTICE, ACTIVITY.PRACTICE,
          ACTIVITY.TARGETED, ACTIVITY.REVIEW,
        ]
      : bucket === 'practice'
      ? [
          ACTIVITY.PRACTICE, ACTIVITY.PRACTICE,
          ACTIVITY.TARGETED, ACTIVITY.TARGETED,
          ACTIVITY.FULL, ACTIVITY.REVIEW,
        ]
      : /* polish */
        [
          ACTIVITY.FULL, ACTIVITY.FULL,
          ACTIVITY.TARGETED, ACTIVITY.TARGETED,
          ACTIVITY.REVIEW, ACTIVITY.REVIEW,
        ];
  return order[dayIndex % order.length];
}

// Returns 'study' | 'review' | 'rest' based on the index modulo the cadence cycle.
export function cadenceCell(dayIndex, cadence) {
  const onLen = Math.max(0, cadence.days_on);
  const revLen = Math.max(0, cadence.review_days);
  const cycleLen = onLen + revLen + Math.max(0, cadence.rest_days);
  if (cycleLen === 0) return 'study';
  const pos = dayIndex % cycleLen;
  if (pos < onLen) return 'study';
  if (pos < onLen + revLen) return 'review';
  return 'rest';
}

// Largest index <= `day` whose cadence cell is 'study'. Day 0 is always a study
// cell, so this always resolves. Used to keep pre-scheduled full sims on study
// cells (otherwise buildNormalPlan drops them).
function studyCellAtOrBefore(day, cadence) {
  for (let j = Math.max(0, day); j >= 0; j--) {
    if (cadenceCell(j, cadence) === 'study') return j;
  }
  return 0;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function extractWeakPhases(readiness) {
  if (!readiness || !Array.isArray(readiness.weakPhases)) return [];
  return readiness.weakPhases.map((w) => w.phase).filter(Boolean);
}

function activity({ examId, date, type, focusPhase = null, count = null, minutes = null, description = '',
                    title = null, sourceReference = null, linkedDomain = null, linkedTask = null }) {
  return {
    scheduled_for:     toIsoDate(date),
    activity_type:     type,
    exam_id:           examId,
    focus_phase:       focusPhase,
    recommended_count: count,
    target_minutes:    minutes,
    description,
    status:            'pending',
    title,
    source_reference:  sourceReference,
    linked_domain:     linkedDomain,
    linked_task:       linkedTask,
    linked_focus_area: null,
    linked_question_filter: null,
  };
}

function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return startOfUtcDay(v);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return startOfUtcDay(d);
}
function startOfUtcDay(d) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())); }
function addDays(d, n) { return new Date(d.getTime() + n * 86400000); }
function daysBetween(a, b) { return Math.round((b.getTime() - a.getTime()) / 86400000); }
function toIsoDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Persistence ─────────────────────────────────────────────────────────────

/**
 * Save a freshly-generated plan: archives the existing active plan (if any)
 * and inserts the new plan + its activities.
 *
 * @param {object} params
 * @param {string} params.examId
 * @param {string} params.targetDate  — ISO date
 * @param {object[]} params.activities
 * @param {object} [params.inputsSnapshot]
 * @returns {Promise<{ plan_id: string|null, count: number, error?: string }>}
 */
export async function saveStudyPlan({ examId, targetDate, activities, inputsSnapshot }) {
  try {
    // 0. Resolve the calling user's id. RLS WITH CHECK on study_plans is
    //    auth.uid() = user_id, so the insert MUST include the user_id —
    //    the database doesn't fill it in automatically.
    let userId = null;
    try {
      // Local session read (no network); RLS still enforces auth.uid() = user_id.
      const { data: authData } = await supabase.auth.getSession();
      userId = authData?.session?.user?.id || null;
    } catch (_) {
      userId = null;
    }
    if (!userId) {
      const msg = 'Not signed in — cannot save a plan.';
      console.warn('[studyPlanService] ' + msg);
      return { plan_id: null, count: 0, error: msg };
    }

    // 1. Archive existing active plan (RPC; idempotent).
    const { error: arErr } = await supabase.rpc('archive_active_study_plan', { p_exam_id: examId });
    if (arErr) {
      console.warn('[studyPlanService] archive_active failed:', arErr.message);
      // Don't bail — the new insert might still succeed if there was no active plan.
    }

    // 2. Insert the new plan.
    const { data: planRow, error: planErr } = await supabase
      .from('study_plans')
      .insert({
        user_id:          userId,
        exam_id:          examId,
        target_exam_date: targetDate,
        inputs_snapshot:  inputsSnapshot || null,
        status:           'active',
      })
      .select()
      .single();

    if (planErr || !planRow?.id) {
      console.warn('[studyPlanService] plan insert failed:', planErr?.message);
      return { plan_id: null, count: 0, error: planErr?.message || 'plan insert failed' };
    }

    // 3. Insert activities.
    const rows = (activities || []).map((a) => ({ ...a, plan_id: planRow.id }));
    if (rows.length > 0) {
      const { error: actErr } = await supabase.from('study_plan_activities').insert(rows);
      if (actErr) {
        console.warn('[studyPlanService] activities insert failed:', actErr.message);
        return { plan_id: planRow.id, count: 0, error: actErr.message };
      }
    }

    return { plan_id: planRow.id, count: rows.length };
  } catch (err) {
    console.warn('[studyPlanService] unexpected save error:', err?.message || err);
    return { plan_id: null, count: 0, error: err?.message || String(err) };
  }
}

/**
 * Load the learner's active plan for a cert, with activities sorted by date.
 *
 * @param {string} examId
 * @returns {Promise<{ plan: object|null, activities: object[] }>}
 */
export async function loadActiveStudyPlan(examId) {
  try {
    const { data: plan, error: pErr } = await supabase
      .from('study_plans')
      .select('*')
      .eq('exam_id', examId)
      .eq('status', 'active')
      .order('generated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (pErr || !plan) {
      if (pErr) console.warn('[studyPlanService] load plan failed:', pErr.message);
      return { plan: null, activities: [] };
    }

    const { data: acts, error: aErr } = await supabase
      .from('study_plan_activities')
      .select('*')
      .eq('plan_id', plan.id)
      .order('scheduled_for', { ascending: true });

    if (aErr) {
      console.warn('[studyPlanService] load activities failed:', aErr.message);
      return { plan, activities: [] };
    }
    return { plan, activities: acts || [] };
  } catch (err) {
    console.warn('[studyPlanService] unexpected load error:', err?.message || err);
    return { plan: null, activities: [] };
  }
}

/**
 * Mark an activity completed (or pending). Manual flow in Phase D.
 */
export async function setActivityStatus(activityId, status) {
  if (!activityId || !['pending', 'completed', 'skipped'].includes(status)) return false;
  try {
    const patch = { status, completed_at: status === 'completed' ? new Date().toISOString() : null };
    const { error } = await supabase
      .from('study_plan_activities')
      .update(patch)
      .eq('id', activityId);
    if (error) {
      console.warn('[studyPlanService] activity status update failed:', error.message);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[studyPlanService] unexpected status error:', err?.message || err);
    return false;
  }
}

/**
 * Find today's planned activity (or the next upcoming one if today's is
 * already completed) for the active plan.
 */
export function pickTodaysActivity(activities, todayIso) {
  if (!Array.isArray(activities) || activities.length === 0) return null;
  const todays = activities.find((a) => a.scheduled_for === todayIso && a.status === 'pending');
  if (todays) return todays;
  // Otherwise the next pending activity scheduled on/after today.
  return activities.find((a) => a.scheduled_for >= todayIso && a.status === 'pending') || null;
}
